import apiClient from './apiClient';

/**
 * Get the current disclaimer content (public endpoint)
 * @param {string} type - Disclaimer type (default: 'platform')
 * @returns {Promise<Object>} Current disclaimer data
 */
export const getCurrentDisclaimer = async (type = 'platform') => {
  const response = await apiClient.get('/disclaimer/current', {
    params: { type },
  });
  return response.data?.disclaimer || null;
};

/**
 * Get the user's disclaimer acceptance status (authenticated)
 * @param {string} type - Disclaimer type (default: 'platform')
 * @returns {Promise<Object>} Acceptance status
 */
export const getDisclaimerStatus = async (type = 'platform') => {
  const response = await apiClient.get('/disclaimer/status', {
    params: { type },
  });
  return response.data?.status || null;
};

/**
 * Accept the current disclaimer (authenticated)
 * @param {string} version - Disclaimer version being accepted
 * @param {string} contentHash - Hash of the disclaimer content
 * @param {string} disclaimerType - Type of disclaimer (default: 'platform')
 * @returns {Promise<Object>} Acceptance record
 */
export const acceptDisclaimer = async (version, contentHash, disclaimerType = 'platform') => {
  const response = await apiClient.post('/disclaimer/accept', {
    version,
    contentHash,
    disclaimerType,
  });
  return response.data?.acceptance || null;
};

// Admin API functions

/**
 * List all disclaimer versions (admin only)
 * @returns {Promise<Array>} List of disclaimer versions
 */
export const listDisclaimerVersions = async () => {
  const response = await apiClient.get('/admin/disclaimer/versions');
  return response.data?.versions || [];
};

/**
 * Create a new disclaimer version (admin only)
 * @param {Object} disclaimerData - Disclaimer data
 * @returns {Promise<Object>} Created disclaimer
 */
export const createDisclaimerVersion = async (disclaimerData) => {
  const response = await apiClient.post('/admin/disclaimer/versions', disclaimerData);
  return response.data?.disclaimer || null;
};

/**
 * Update an existing disclaimer version (admin only)
 * @param {string} version - Version to update
 * @param {Object} updates - Fields to update
 * @returns {Promise<Object>} Updated disclaimer
 */
export const updateDisclaimerVersion = async (version, updates) => {
  const response = await apiClient.put(`/admin/disclaimer/versions/${encodeURIComponent(version)}`, updates);
  return response.data?.disclaimer || null;
};

/**
 * Get disclaimer acceptances (admin only)
 * @param {Object} filters - Optional filters (version, accountId, limit)
 * @returns {Promise<Object>} Acceptances data
 */
export const getDisclaimerAcceptances = async (filters = {}) => {
  const response = await apiClient.get('/admin/disclaimer/acceptances', {
    params: filters,
  });
  return response.data || {};
};
